/**
 * BeneficiaryPicker — shared primitive with 3 dispatch modes.
 *
 * Ports legacy `components/products/BeneficiaryPicker.tsx` (426L) down to a
 * demo-viable surface. The legacy picker handled 4 sources (cart / prior /
 * CIF / manual). This port keeps the core mechanism dispatch + percent
 * validation and ships a minimal-viable inline-form CRUD. Deeper features
 * (cart-person carry-forward, prior-session quick-add, CIF inline search)
 * are deferred to Wave 4 per prompt guidance.
 *
 * Dispatch modes (one component, three mechanisms)
 * ------------------------------------------------
 *   IRA       (acctType '60')        → TaxPlnBenfAdd            primary/contingent
 *   Individual (acctType '10'/'20'/'30') → CustRelAdd + BP/BC   primary/contingent
 *   Business  (acctType omitted, mechanism='business') → CustRelAdd + LM/OB/TR + ownership%/Controller
 *
 * Mechanism cross-reference (memory/benef-architecture-v3.md §"Mechanisms by account category"):
 *   IRA             TaxPlnBenfAdd      field 282 BenfDesigId (1=Primary, 2=Contingent)
 *   DDA/SAV/CD POD  CustRelAdd BP/BC   (Scott Bond added 2026-04-20 MNTUSR)
 *   Business BO     CustRelAdd LM/OB/TR + ownership% + Controller flag
 *
 * Validation:
 *   - For IRA/Individual modes, primary beneficiaries must sum to 100%.
 *     Contingents must sum to 100% (or 0 if none captured).
 *   - For Business mode, ownership% across members cannot exceed 100%.
 *
 * Hard rules followed:
 *   - Uses existing primitives (Button, Pill, Banner) + utils/ssn.ts for display.
 *   - Internal SSN/EIN is stored raw (no mask) per feedback_ssn_masking.
 *   - DOB is YYYY-MM-DD; emitted to callers unchanged so consumers can route
 *     through utils/jxDates.toNamedJsonDate() before hitting jXRL.
 *   - SDB (acctType '50') renders a short note — SDB uses deputy access, not beneficiaries.
 */

import { useMemo, useState } from 'react';
import { Button } from './Button';
import { Pill } from './Pill';
import { Banner } from './Banner';
import { formatSsn } from '@/utils/ssn';

// -----------------------------------------------------------------------------
// Public types
// -----------------------------------------------------------------------------

/** Which Core Director operation the entry will flow through at commit time. */
export type BeneficiaryMechanism =
  | 'TaxPlnBenf'      // IRA → TaxPlnBenfAdd
  | 'CustRelAdd-BP'   // Individual Primary   (POD Primary)
  | 'CustRelAdd-BC'   // Individual Contingent (POD Contingent)
  | 'CustRelAdd-LM'   // Business Limited Member / Member
  | 'CustRelAdd-OB'   // Business Beneficial Owner
  | 'CustRelAdd-TR';  // Business/Trust Trustee

/** Role buckets used for UI grouping and percent validation. */
export type BeneficiaryRole =
  | 'primary'
  | 'contingent'
  | 'member'
  | 'owner'
  | 'trustee';

export interface BeneficiaryEntry {
  /** Local UUID. Stable across rerenders; persists via onChange. */
  id: string;
  /** Present when the beneficiary is a pre-existing CIF (future hydrate path). */
  cifId?: string;
  /** Core Director operation + RelCode this entry will commit through. */
  mechanism: BeneficiaryMechanism;
  /** UI group. Drives percent-sum validation. */
  role: BeneficiaryRole;
  firstName: string;
  /** Optional middle initial/name (Bill 2026-07-13 Item 16) — rides into the
   *  POD CIF create + Core Director name field at commit. */
  middleName?: string;
  lastName: string;
  /** Free-text relationship (Spouse / Child / Parent / Trust / Charity / Other). */
  relationship: string;
  /** YYYY-MM-DD. */
  dob?: string;
  /** Raw SSN or EIN digits (no mask) per feedback_ssn_masking. */
  taxId?: string;
  /**
   * Beneficiary has NO SSN/TIN/ATIN (Bill 2026-07-10, BACKLOG-0863).
   * When true the SSN input is disabled + cleared, the required-SSN
   * validation is satisfied, and the commit-side POD CIF create runs the
   * existing Core Director Field 92 TINCode='N' no-TIN CustAdd rail
   * (empty TaxId — Core generates the CIF).
   */
  noSsn?: boolean;
  /**
   * Contact phone, digits only (Bill 2026-07-10, BACKLOG-0863). Destination:
   * CustAdd Phone (CartCommitService.CustAddIndividualAsync) when the POD
   * beneficiary CIF is created at commit; online-app imports populate it
   * from the legacy list's Beneficiary Phone column (OnlineAppImportService).
   */
  phone?: string;
  /**
   * For IRA/Individual: distribution percent (0-100).
   * For Business members: ownership percent (0-100).
   */
  percentage: number;
  /** Business only — FinCEN controller flag (CEO / Managing Member / Partner). */
  isController?: boolean;
  /** Business only — display title for the controller or owner. */
  title?: string;
  address?: string;
  /**
   * Minor beneficiary's custodian (Bill 2026-09-30, BACKLOG-1648): the adult
   * who receives the minor payee's share under the Missouri Transfers to
   * Minors Law. Name, address and phone are REQUIRED for a minor beneficiary;
   * email is optional. Printed on the PCB POD designation forms (v21.2 /
   * custodian-for-minors v21.0) — there is no Core Director wire field.
   */
  custodianName?: string;
  custodianAddress?: string;
  /** Digits only. */
  custodianPhone?: string;
  custodianEmail?: string;
  /** Optional successor custodian — same PCB form block. */
  successorCustodianName?: string;
  successorCustodianAddress?: string;
  successorCustodianPhone?: string;
  successorCustodianEmail?: string;
}

/** Dispatch mode. Follows prompt contract exactly. */
export type BeneficiaryAcctType = '10' | '20' | '30' | '50' | '60';

export interface BeneficiaryPickerProps {
  /**
   * Deposit/loan account type code. Routes IRA vs Individual vs none.
   * Mutually exclusive with `mechanism='business'` — pass that instead for
   * Business BO capture.
   */
  accountType: BeneficiaryAcctType;
  /**
   * Override for Business BO capture (Corp / LLC / Partnership entity).
   * When set, the UI flips to Members/Owners/Trustees with ownership%.
   */
  businessMode?: boolean;
  /** Owner CIF (for display context; not used in persistence today). */
  ownerCifId?: string;
  /** Hydrate from persisted state on mount. */
  initialBeneficiaries?: ReadonlyArray<BeneficiaryEntry>;
  /** Emits full list on every mutation (per prompt contract). */
  onChange: (beneficiaries: BeneficiaryEntry[]) => void;
  /** Primary bucket cap (default 10). */
  maxPrimary?: number;
  /** Contingent bucket cap (default 10). */
  maxContingent?: number;
  /** Disable editing (read-only post-commit). */
  disabled?: boolean;
}

// -----------------------------------------------------------------------------
// Internals
// -----------------------------------------------------------------------------

function genId(): string {
  // Demo-safe unique enough for local keys; not a server ID.
  return `b_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function dispatchForRole(
  role: BeneficiaryRole,
  businessMode: boolean,
  acctType: BeneficiaryAcctType,
): BeneficiaryMechanism {
  if (businessMode) {
    if (role === 'trustee') return 'CustRelAdd-TR';
    if (role === 'owner')   return 'CustRelAdd-OB';
    return 'CustRelAdd-LM';
  }
  if (acctType === '60') return 'TaxPlnBenf';
  return role === 'primary' ? 'CustRelAdd-BP' : 'CustRelAdd-BC';
}

function emptyEntry(
  role: BeneficiaryRole,
  businessMode: boolean,
  acctType: BeneficiaryAcctType,
): BeneficiaryEntry {
  return {
    id: genId(),
    mechanism: dispatchForRole(role, businessMode, acctType),
    role,
    firstName: '',
    lastName: '',
    relationship: businessMode ? '' : 'Spouse',
    percentage: 0,
  };
}

// -----------------------------------------------------------------------------
// Small styled inputs (brand-token friendly; avoid depending on any CSS files)
// -----------------------------------------------------------------------------

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '7px 9px',
  border: '1px solid var(--ink-200)',
  borderRadius: 4,
  fontSize: 13,
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 10,
  color: 'var(--ink-500)',
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
  marginBottom: 3,
  fontWeight: 600,
};

const RELATIONSHIPS: ReadonlyArray<string> = [
  'Spouse', 'Child', 'Parent', 'Sibling', 'Grandchild',
  'Trust', 'Estate', 'Charity', 'Other',
];

const BUSINESS_TITLES: ReadonlyArray<string> = [
  'CEO', 'CFO', 'COO', 'President', 'Vice President',
  'Managing Member', 'Partner', 'General Partner', 'Trustee', 'Other',
];

// -----------------------------------------------------------------------------
// Row editor — inline expand, not a modal (per prompt)
// -----------------------------------------------------------------------------

interface RowProps {
  entry: BeneficiaryEntry;
  onPatch: (patch: Partial<BeneficiaryEntry>) => void;
  onRemove: () => void;
  businessMode: boolean;
  disabled: boolean;
}

function BenfRow({ entry, onPatch, onRemove, businessMode, disabled }: RowProps): JSX.Element {
  const pctLabel = businessMode ? 'Ownership %' : '%';
  const isMinor = (() => {
    if (!entry.dob) return false;
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(entry.dob);
    if (!m) return false;
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const d = Number(m[3]);
    const now = new Date();
    let age = now.getFullYear() - y;
    const cm = now.getMonth() + 1;
    const cd = now.getDate();
    if (cm < mo || (cm === mo && cd < d)) age -= 1;
    return age >= 0 && age < 18;
  })();

  return (
    <div
      style={{
        background: 'var(--ink-50)',
        borderRadius: 'var(--r-sm)',
        padding: 'var(--sp-5)',
        marginBottom: 'var(--sp-4)',
        border: '1px solid var(--ink-100)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Pill variant={entry.role === 'primary' ? 'ok' : entry.role === 'contingent' ? 'info' : 'muted'}>
            {entry.role === 'primary' ? 'Primary'
              : entry.role === 'contingent' ? 'Contingent'
              : entry.role === 'owner' ? 'Owner'
              : entry.role === 'trustee' ? 'Trustee'
              : 'Member'}
          </Pill>
          <span style={{ fontSize: 11, color: 'var(--ink-500)', fontFamily: 'var(--font-mono)' }}>
            {entry.mechanism}
          </span>
          {businessMode && entry.isController && <Pill variant="warn">Controller</Pill>}
          {isMinor && <Pill variant="warn">Minor</Pill>}
        </div>
        {!disabled && (
          <Button variant="ghost" size="sm" onClick={onRemove}>Remove</Button>
        )}
      </div>

      {/* Row 1 — Name, SSN/EIN, DOB, percent */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
          gap: 'var(--sp-4)',
          marginBottom: 'var(--sp-4)',
        }}
      >
        <div>
          <label style={labelStyle}>First name</label>
          <input
            style={inputStyle}
            value={entry.firstName}
            onChange={(e) => onPatch({ firstName: e.currentTarget.value })}
            disabled={disabled}
            aria-label="First name"
          />
        </div>
        <div>
          <label style={labelStyle}>Last name</label>
          <input
            style={inputStyle}
            value={entry.lastName}
            onChange={(e) => onPatch({ lastName: e.currentTarget.value })}
            disabled={disabled}
            aria-label="Last name"
          />
        </div>
        <div>
          <label style={labelStyle}>{businessMode ? 'EIN / SSN' : 'SSN / TIN'}</label>
          <input
            style={inputStyle}
            value={entry.taxId ?? ''}
            onChange={(e) => onPatch({ taxId: e.currentTarget.value.replace(/\D/g, '').slice(0, 9) })}
            placeholder="9 digits"
            maxLength={9}
            disabled={disabled}
            aria-label="Tax ID"
          />
          {entry.taxId && entry.taxId.length === 9 && (
            <div style={{ fontSize: 10, color: 'var(--ink-500)', marginTop: 2 }}>
              {formatSsn(entry.taxId)}
            </div>
          )}
        </div>
        <div>
          <label style={labelStyle}>DOB</label>
          <input
            type="date"
            style={inputStyle}
            value={entry.dob ?? ''}
            onChange={(e) => onPatch({ dob: e.currentTarget.value })}
            disabled={disabled}
            aria-label="Date of birth"
          />
        </div>
        <div>
          <label style={labelStyle}>{pctLabel}</label>
          <input
            type="number"
            min={0}
            max={100}
            step="0.01"
            style={inputStyle}
            value={Number.isFinite(entry.percentage) ? entry.percentage : 0}
            onChange={(e) => onPatch({ percentage: Number.parseFloat(e.currentTarget.value) || 0 })}
            disabled={disabled}
            aria-label={pctLabel}
          />
        </div>
      </div>

      {/* Row 2 — Relationship / Title, Address */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
          gap: 'var(--sp-4)',
        }}
      >
        {businessMode ? (
          <div>
            <label style={labelStyle}>Title</label>
            <select
              style={inputStyle}
              value={entry.title ?? ''}
              onChange={(e) => onPatch({ title: e.currentTarget.value })}
              disabled={disabled}
              aria-label="Title"
            >
              <option value="">Select…</option>
              {BUSINESS_TITLES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        ) : (
          <div>
            <label style={labelStyle}>Relationship</label>
            <select
              style={inputStyle}
              value={entry.relationship}
              onChange={(e) => onPatch({ relationship: e.currentTarget.value })}
              disabled={disabled}
              aria-label="Relationship"
            >
              {RELATIONSHIPS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        )}
        <div>
          <label style={labelStyle}>Address (optional)</label>
          <input
            style={inputStyle}
            value={entry.address ?? ''}
            onChange={(e) => onPatch({ address: e.currentTarget.value })}
            disabled={disabled}
            aria-label="Address"
          />
        </div>
        {businessMode && (
          <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 4 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: disabled ? 'default' : 'pointer' }}>
              <input
                type="checkbox"
                checked={!!entry.isController}
                onChange={(e) => onPatch({ isController: e.currentTarget.checked })}
                disabled={disabled}
              />
              FinCEN Controller
            </label>
          </div>
        )}
      </div>

      {isMinor && !businessMode && (
        <div style={{ marginTop: 8 }}>
          <Banner tone="warn" title="Minor beneficiary">
            Custodian required. A UGMA/UTMA custodian must be captured at signing.
          </Banner>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Main primitive
// -----------------------------------------------------------------------------

/** Primitive: BeneficiaryPicker (shared across RAO/RAM/RCM/RIM). */
export function BeneficiaryPicker({
  accountType,
  businessMode = false,
  ownerCifId,
  initialBeneficiaries,
  onChange,
  maxPrimary = 10,
  maxContingent = 10,
  disabled = false,
}: BeneficiaryPickerProps): JSX.Element {
  const [entries, setEntries] = useState<BeneficiaryEntry[]>(
    () => (initialBeneficiaries ? initialBeneficiaries.map((e) => ({ ...e })) : []),
  );

  // SDB short-circuit — beneficiaries are not the right pattern for safe deposit.
  if (accountType === '50' && !businessMode) {
    return (
      <Banner tone="info" title="Safe-deposit box">
        SDB accounts use a <strong>deputy access</strong> pattern, not beneficiary
        designation. Add deputies during box setup in the RSM module.
      </Banner>
    );
  }

  const commit = (next: BeneficiaryEntry[]): void => {
    setEntries(next);
    onChange(next);
  };

  const patchEntry = (id: string, patch: Partial<BeneficiaryEntry>): void => {
    commit(entries.map((e) => (e.id === id ? { ...e, ...patch } : e)));
  };

  const removeEntry = (id: string): void => {
    commit(entries.filter((e) => e.id !== id));
  };

  const addPrimary = (): void => {
    if (entries.filter((e) => e.role === 'primary').length >= maxPrimary) return;
    commit([...entries, emptyEntry('primary', businessMode, accountType)]);
  };
  const addContingent = (): void => {
    if (entries.filter((e) => e.role === 'contingent').length >= maxContingent) return;
    commit([...entries, emptyEntry('contingent', businessMode, accountType)]);
  };
  const addMember = (role: BeneficiaryRole): void => {
    commit([...entries, emptyEntry(role, true, accountType)]);
  };

  const equalSplit = (role: BeneficiaryRole): void => {
    const bucket = entries.filter((e) => e.role === role);
    if (bucket.length === 0) return;
    const each = Math.floor((100 / bucket.length) * 100) / 100;
    const allocated = each * (bucket.length - 1);
    const last = Math.round((100 - allocated) * 100) / 100;
    let i = 0;
    commit(
      entries.map((e) => {
        if (e.role !== role) return e;
        const isLast = i === bucket.length - 1;
        const pct = isLast ? last : each;
        i += 1;
        return { ...e, percentage: pct };
      }),
    );
  };

  // ---- Validation (derived)
  const primaryEntries = useMemo(() => entries.filter((e) => e.role === 'primary'), [entries]);
  const contingentEntries = useMemo(() => entries.filter((e) => e.role === 'contingent'), [entries]);
  const businessEntries = useMemo(
    () => entries.filter((e) => e.role === 'member' || e.role === 'owner' || e.role === 'trustee'),
    [entries],
  );

  const sum = (arr: BeneficiaryEntry[]): number =>
    Math.round(arr.reduce((s, e) => s + (Number.isFinite(e.percentage) ? e.percentage : 0), 0) * 100) / 100;

  const primaryTotal = sum(primaryEntries);
  const contingentTotal = sum(contingentEntries);
  const businessOwnershipTotal = sum(businessEntries);
  const hasController = businessEntries.some((e) => e.isController);

  // ---- Header text
  const headerLabel = businessMode
    ? 'Beneficial Owners'
    : accountType === '60'
      ? 'IRA Beneficiaries'
      : 'POD Beneficiaries';

  const headerMechanismNote = businessMode
    ? 'CustRelAdd · LM / OB / TR · ownership % + Controller flag'
    : accountType === '60'
      ? 'TaxPlnBenfAdd · Primary + Contingent (field 282 BenfDesigId)'
      : 'CustRelAdd · BP (Primary) + BC (Contingent)';

  return (
    <div>
      <div style={{ marginBottom: 'var(--sp-4)' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
          <strong style={{ color: 'var(--pcb-blue-dark)', fontSize: 13 }}>{headerLabel}</strong>
          <span style={{ fontSize: 11, color: 'var(--ink-500)', fontFamily: 'var(--font-mono)' }}>{headerMechanismNote}</span>
        </div>
        {ownerCifId && (
          <div style={{ fontSize: 11, color: 'var(--ink-600)', marginTop: 2 }}>
            Owner CIF <strong>{ownerCifId}</strong>
          </div>
        )}
      </div>

      {/* Business BO layout */}
      {businessMode ? (
        <>
          {businessEntries.map((e) => (
            <BenfRow
              key={e.id}
              entry={e}
              onPatch={(patch) => patchEntry(e.id, patch)}
              onRemove={() => removeEntry(e.id)}
              businessMode
              disabled={disabled}
            />
          ))}
          {!disabled && (
            <div style={{ display: 'flex', gap: 'var(--sp-3)', flexWrap: 'wrap', marginTop: 'var(--sp-4)' }}>
              <Button variant="ghost" size="sm" onClick={() => addMember('owner')}>+ Owner (≥25%)</Button>
              <Button variant="ghost" size="sm" onClick={() => addMember('member')}>+ Member</Button>
              <Button variant="ghost" size="sm" onClick={() => addMember('trustee')}>+ Trustee</Button>
            </div>
          )}
          <div style={{ marginTop: 'var(--sp-5)', display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
            {businessOwnershipTotal > 100 && (
              <Banner tone="err">
                Total ownership is {businessOwnershipTotal}% — must not exceed 100%.
              </Banner>
            )}
            {!hasController && businessEntries.length > 0 && (
              <Banner tone="warn" title="Controller required">
                FinCEN CDD requires at least one Controller (CEO / Managing Member / Partner).
              </Banner>
            )}
            {businessEntries.length === 0 && (
              <Banner tone="info">
                Identify every individual with 25%+ ownership plus one Controller. A
                BO Certification form is required at signing.
              </Banner>
            )}
          </div>
        </>
      ) : (
        // IRA / Individual layout — Primary + Contingent buckets
        <>
          {/* Primary */}
          <div style={{ marginBottom: 'var(--sp-6)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--sp-3)' }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-700)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                Primary beneficiaries
              </span>
              <div style={{ display: 'flex', gap: 'var(--sp-3)' }}>
                {primaryEntries.length > 1 && !disabled && (
                  <Button variant="link" size="sm" onClick={() => equalSplit('primary')}>Equal split</Button>
                )}
                {!disabled && primaryEntries.length < maxPrimary && (
                  <Button variant="ghost" size="sm" onClick={addPrimary}>+ Primary</Button>
                )}
              </div>
            </div>
            {primaryEntries.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--ink-600)', padding: 'var(--sp-4)', border: '1px dashed var(--ink-200)', borderRadius: 'var(--r-sm)' }}>
                No primary beneficiaries yet. Add one to continue.
              </div>
            ) : (
              primaryEntries.map((e) => (
                <BenfRow
                  key={e.id}
                  entry={e}
                  onPatch={(patch) => patchEntry(e.id, patch)}
                  onRemove={() => removeEntry(e.id)}
                  businessMode={false}
                  disabled={disabled}
                />
              ))
            )}
          </div>

          {/* Contingent */}
          <div style={{ marginBottom: 'var(--sp-5)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--sp-3)' }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-700)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                Contingent beneficiaries
              </span>
              <div style={{ display: 'flex', gap: 'var(--sp-3)' }}>
                {contingentEntries.length > 1 && !disabled && (
                  <Button variant="link" size="sm" onClick={() => equalSplit('contingent')}>Equal split</Button>
                )}
                {!disabled && contingentEntries.length < maxContingent && (
                  <Button variant="ghost" size="sm" onClick={addContingent}>+ Contingent</Button>
                )}
              </div>
            </div>
            {contingentEntries.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--ink-500)' }}>
                Contingent beneficiaries are optional. They inherit only if all primaries predecease.
              </div>
            ) : (
              contingentEntries.map((e) => (
                <BenfRow
                  key={e.id}
                  entry={e}
                  onPatch={(patch) => patchEntry(e.id, patch)}
                  onRemove={() => removeEntry(e.id)}
                  businessMode={false}
                  disabled={disabled}
                />
              ))
            )}
          </div>

          {/* Validation banners */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
            {primaryEntries.length > 0 && primaryTotal !== 100 && (
              <Banner tone="err">
                Primary beneficiaries total {primaryTotal}% — must equal 100%.
              </Banner>
            )}
            {contingentEntries.length > 0 && contingentTotal !== 100 && (
              <Banner tone="err">
                Contingent beneficiaries total {contingentTotal}% — must equal 100%.
              </Banner>
            )}
            {primaryEntries.length > 0 && primaryTotal === 100 && (
              <Banner tone="ok">
                Primary: {primaryEntries.length} beneficiar{primaryEntries.length > 1 ? 'ies' : 'y'} · 100%
                {contingentEntries.length > 0 && ` · Contingent: ${contingentEntries.length} · ${contingentTotal}%`}
              </Banner>
            )}
          </div>
        </>
      )}
    </div>
  );
}
